"use client";

import { BadgeCheck, LogOut, Settings } from "lucide-react";
import Link from "@/components/ui/Link";
import { useEffect, useRef, useState } from "react";
import { LangField } from "@/components/forms/LangField";
import { Avatar } from "@/components/ui/Avatar";
import { useUi } from "@/components/ui/use-ui";
import { signOutAction } from "@/lib/auth/actions";
import { clearAllLists } from "@/lib/library";

interface MenuUser {
  displayName: string;
  username: string;
  email: string | null;
  avatar: string | null;
}

/** Avatar button in the header with a glass dropdown (beeg keeps a person icon in the same spot). */
export function AccountMenu({ user }: { user: MenuUser }) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const t = useUi().account;

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => !root.current?.contains(e.target as Node) && setOpen(false);
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const item = "flex items-center gap-3 rounded-lg px-3 py-2.5 text-[14px] text-soft transition-colors hover:bg-white/[0.08] hover:text-fg";
  const close = () => setOpen(false);

  return (
    <div ref={root} className="relative">
      <button
        type="button"
        aria-label={t.menu}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="grid size-11 place-items-center rounded-full transition-colors hover:bg-white/10"
      >
        <Avatar name={user.displayName} seed={user.username} src={user.avatar} size={32} />
      </button>
      {open && (
        <div role="menu" className="glass-strong absolute right-0 top-full z-40 mt-2 w-64 rounded-2xl p-1.5 shadow-[0_24px_70px_-20px_rgb(0_0_0/0.9)]">
          <div className="flex items-center gap-3 px-3 pb-3 pt-2.5">
            <Avatar name={user.displayName} seed={user.username} src={user.avatar} size={40} />
            <div className="min-w-0">
              <p className="truncate text-[15px] text-fg">{user.displayName}</p>
              <p className="truncate text-[12.5px] text-muted">{user.email ?? `@${user.username}`}</p>
            </div>
          </div>
          <div className="mb-1 border-t border-line" />
          <Link href="/account" role="menuitem" className={item} onClick={close}>
            <Settings size={17} /> {t.settings}
          </Link>
          <Link href="/account/profiles" role="menuitem" className={item} onClick={close}>
            <BadgeCheck size={17} /> {t.profiles}
          </Link>
          <form action={signOutAction} onSubmit={() => clearAllLists()}>
            <LangField />
            <button type="submit" role="menuitem" className={`${item} w-full`}>
              <LogOut size={17} /> {t.logOut}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
